import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import BundleOverviewController from 'components/features/BundleOverviewController/BundleOverviewController';
import { Map, Marker, TileLayer } from 'react-leaflet';
import { distillProductType, getDonorUnitFromBundleMembers } from 'common/utils/helpers';
import DeviceDetailsPanel from 'components/features/DeviceDetailsPanel/DeviceDetailsPanel';
import DeviceDetailsGa2Panel from 'components/features/DeviceDetailsPanel/DeviceDetailsGa2Panel';
import AlarmHistoryPanel from 'components/features/AlarmHistoryPanel/AlarmHistoryPanel';
import { MaintenancePanelBar } from 'components/features/MaintenancePanelBar/MaintenancePanelBar';
import { deviceIcon } from '../../../common/deviceMapIcons';
import { getGa2DeviceMembers } from '../../../common/utils/deviceTwinUtils';
import {
  defaultRuntimeStatuses,
  defaultTimeRange
} from 'components/features/BundleOverviewController/BundleOverviewControllerDMActions/utils';
import { ActionsInfoPanel } from 'components/features/ActionsInfoPanel';
import { useGetActionsQuery } from 'apis/cm';
import './DeviceTypePageTemplate.css';
import { updatePivotOverviewPageActiveTab } from 'redux/actions';

const currentDate = new Date();

const DeviceTypePageTemplate = (props) => {
  const { deviceType, isGa2 } = props;
  const history = useHistory();
  const dispatch = useDispatch();
  const echoDevice = useSelector((state) => state.ui.echoOverviewPage.selectedDevice);
  const pivotDevices = useSelector((state) => state.ui.pivotOverviewPage.selectedDevices);
  // props.device from pivot overview page is DU
  // props.device from echo overview page is echo device
  const targetDevice = deviceType === 'pivot' ? pivotDevices?.[props.device?.id] : echoDevice;
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);

  useEffect(() => {
    return () => {
      dispatch(updatePivotOverviewPageActiveTab(''));
    };
  }, []);

  const ga2PivotDevices = useMemo(
    () =>
      getGa2DeviceMembers(targetDevice).reduce((acc, ga2Device) => {
        acc[ga2Device.id] = ga2Device;

        return acc;
      }, {}),
    [targetDevice]
  );

  const selectedDevice = selectedDeviceId
    ? selectedDeviceId === (targetDevice.deviceId ?? targetDevice.id)
      ? targetDevice
      : isGa2
      ? ga2PivotDevices[selectedDeviceId]
      : pivotDevices[selectedDeviceId]
    : null;

  const allDeviceIds = useMemo(() => {
    if (isGa2) {
      const duId = targetDevice.id;
      const suIds = Object.keys(ga2PivotDevices || {});
      return [duId, ...suIds];
    } else if (deviceType === 'pivot') {
      const deviceIds = props.bundleMembers?.map((member) => member.id);
      return deviceIds;
    } else if (deviceType === 'echo') {
      return [targetDevice?.id];
    }
    return [];
  }, [isGa2, targetDevice, deviceType, ga2PivotDevices, props.bundleMembers]);

  const handleSelectDevice = useCallback(
    (nextSelectedDevice) => {
      const deviceId = nextSelectedDevice?.deviceId || nextSelectedDevice?.id;
      const selectedDeviceType = deviceType === 'pivot' ? 'pivot' : 'echo';
      let url = `/console/devices/${selectedDeviceType}/overview?deviceId=${targetDevice?.id}`;

      if (deviceId) {
        url += `&detail=${deviceId}`;
      }

      setSelectedDeviceId(deviceId);
      dispatch(updatePivotOverviewPageActiveTab(deviceId));
      history.push(url);
    },
    [history, deviceType, targetDevice]
  );

  const divIcon = useMemo(
    () =>
      deviceIcon({
        deviceType,
        className: 'DeviceMap_DivIcon'
      }),
    [deviceType]
  );

  const map = (
    <div id="DeviceTypePageTemplateMap" className="DeviceTypePageTemplateMap">
      <Map
        style={{ height: '100%' }}
        center={{ lat: props.device?.location?.lat || 0, lng: props.device?.location?.lon || 0 }}
        zoom={10}
        zoomControl={false}
        dragging={false}
        keyboard={false}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker
          position={{ lat: props.device?.location?.lat || 0, lng: props.device?.location?.lon || 0 }}
          icon={divIcon}
        />
      </Map>
      {/* <MapZoomOverlay zoomHandler={this.mapZoomHandle} /> */}
    </div>
  );

  const getDeviceData = () => {
    if (props.isGa2) {
      currentDate.setFullYear(currentDate.getFullYear() - 1);

      return [
        {
          deviceId: props.device.deviceId,
          deviceAz: 'unknown',
          deviceType: 'donor',
          lastCommissionDate: currentDate.toISOString()
        }
      ];
    }

    if (props.deviceType === 'pivot') {
      if (props.bundle && props.bundleMembers) {
        const donor = getDonorUnitFromBundleMembers(props.bundleMembers);

        let deviceIds = donor ? [donor.deviceId] : [];
        let deviceTypes = ['donor'];

        const donorAz = donor?.properties?.reported?.antenna_direction?.beamAzimuth;
        let deviceAzs = [donorAz === undefined ? 'unknown' : donorAz];

        // let deviceCommissionDates = [donor?.properties?.reported?.$metadata?.VDL_Gain?.$lastUpdated];

        props.bundleMembers.forEach((member) => {
          if (!deviceIds.includes(member.deviceId || member.id)) {
            deviceIds.push(member.deviceId || member.id);
            const memberAz = member?.properties?.reported?.antenna_direction?.beamAzimuth;
            deviceAzs.push(memberAz === undefined ? 'unknown' : memberAz);
            deviceTypes.push('service');
            // deviceCommissionDates.push(member?.properties?.reported?.$metadata?.VDL_Gain?.$lastUpdated);
          }
        });

        return deviceIds.map((deviceId, i) => ({
          deviceId,
          deviceAz: deviceAzs[i],
          deviceType: deviceTypes[i],
          lastCommissionDate: props.lastCommissionDate
        }));
      }

      return [];
    }

    if (props.device) {
      const deviceAz = props.device?.properties?.reported?.antenna_direction?.beamAzimuth;
      return [
        {
          deviceId: props.device?.deviceId || props.device?.id,
          deviceAz: deviceAz === undefined ? 'unknown' : deviceAz,
          deviceType: 'echo',
          lastCommissionDate: props.lastCommissionDate
        }
      ];
    }

    return [];
  };

  const isQueryEnabled = useMemo(
    () => allDeviceIds?.length > 0 && allDeviceIds.every((id) => typeof id === 'string' && id.trim() !== ''),
    [allDeviceIds]
  );

  const deviceActionsQuery = useGetActionsQuery(
    {
      devices: allDeviceIds,
      runtimeStatuses: defaultRuntimeStatuses,
      ...defaultTimeRange
    },
    {
      enabled: isQueryEnabled,
      placeholderData: [],
      refetchInterval: defaultTimeRange?.span ? 10_000 : false
    }
  );

  const { data } = deviceActionsQuery;

  const latestActiveAction = useMemo(() => {
    const activeActions =
      data?.filter((action) => action.runtimeStatus === 'Running' || action.runtimeStatus === 'Pending') || [];

    if (activeActions.length === 0) return null;

    return activeActions.reduce((latestAction, currentAction) => {
      if (new Date(currentAction.createdTime) > new Date(latestAction.createdTime)) {
        return currentAction;
      }
      return latestAction;
    });
  }, [data]);

  return (
    <div id="DeviceTypePageTemplate" className="DeviceTypePageTemplate">
      <BundleOverviewController
        device={props.device} // DU or echo device
        deviceType={deviceType}
        isGa2={isGa2}
        serviceDevices={props.serviceDevices} // only apply to pivot overview page
        bundle={props.bundle} // only apply to pivot overview page
        bundleMembers={props.bundleMembers} // only apply to pivot overview page
        sendSelectedDevice={handleSelectDevice}
        latestActiveAction={latestActiveAction}
      />
      <div id="deviceTypePageViewContainer" className="deviceTypePageViewContainer">
        {/*Bundle Overview Mode*/}
        {selectedDevice == null &&
          distillProductType(props.device?.properties?.reported?.productType) === 'subscriber' && (
            <div>
              <MaintenancePanelBar
                targetDevice={props.device}
                source={'overview'}
                targetDeviceType={deviceType}
                isGa2={isGa2}
              />
              {map}
            </div>
          )}
        {selectedDevice == null && distillProductType(props.device?.properties?.reported?.productType) === 'donor' && (
          <div>
            <MaintenancePanelBar
              targetDevice={targetDevice} // DU or echo device
              targetDeviceType={deviceType}
              serviceDevices={props.serviceDevices}
              bundle={props.bundle}
              bundleMembers={props.bundleMembers}
              source={'overview'}
              updateBundle={props.updateBundle}
              isGa2={isGa2}
            />
            <ActionsInfoPanel lastCommissionDate={props.lastCommissionDate} latestActiveAction={latestActiveAction} />
            {map}
          </div>
        )}
        {/*Selected Device Mode*/}
        {selectedDevice != null && selectedDevice?.productType !== 'SERVICE' && (
          <DeviceDetailsPanel
            key={selectedDevice.deviceId || selectedDevice.id}
            device={selectedDevice}
            deviceType={deviceType}
            isGa2={isGa2}
            donorDevice={targetDevice}
          />
        )}

        {/*GA2 Selected SU Device Mode*/}
        {isGa2 && selectedDevice?.productType === 'SERVICE' && (
          <DeviceDetailsGa2Panel
            key={selectedDevice.deviceId || selectedDevice.id}
            device={selectedDevice}
            deviceType={selectedDevice?.productType?.toLowerCase()}
            isGa2={isGa2}
            donorDevice={targetDevice}
          />
        )}

        {/* for pivot/echo overview page */}
        {selectedDevice === null && props.device && <AlarmHistoryPanel deviceData={getDeviceData()} isGa2={isGa2} />}
      </div>
    </div>
  );
};

export default DeviceTypePageTemplate;
